import React, { useState, useEffect, useContext } from 'react';
import { LoginContext } from '../../reactcontext/ReactContext';
import axios from 'axios';
import { FaPlus, FaEdit, FaTrash, FaUsers, FaShieldAlt, FaTimes } from 'react-icons/fa';

const RoleManagement = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const { loginInfo } = useContext(LoginContext);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    roleName: '',
    selectedPermissions: []
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, roleId: null, roleName: '' });

  useEffect(() => {
    if (loginInfo.login && loginInfo.userDetails?.role?.toLowerCase() === 'admin') {
      fetchRoles();
      fetchPermissions();
    }
  }, [loginInfo]);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${apiUrl}/userRole`, {
        headers: { loginToken: localStorage.getItem('loginToken') }
      });
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await axios.get(`${apiUrl}/permission/grouped`, {
        headers: { loginToken: localStorage.getItem('loginToken') }
      });
      setPermissions(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRole) {
        // Update role
        await axios.put(`${apiUrl}/userRole/editRole`, {
          id: editingRole.id,
          roleName: formData.roleName
        }, {
          headers: { loginToken: localStorage.getItem('loginToken') }
        });

        // Update permissions
        await axios.post(`${apiUrl}/permission/assign-role`, {
          roleId: editingRole.id,
          permissionIds: formData.selectedPermissions
        }, {
          headers: { loginToken: localStorage.getItem('loginToken') }
        });

        showSuccessNotification('Role updated successfully!');
      } else {
        // Create new role
        const roleResponse = await axios.post(`${apiUrl}/userRole/addRole`, {
          roleName: formData.roleName
        }, {
          headers: { loginToken: localStorage.getItem('loginToken') }
        });

        // Assign permissions to new role
        if (formData.selectedPermissions.length > 0) {
          await axios.post(`${apiUrl}/permission/assign-role`, {
            roleId: roleResponse.data.id,
            permissionIds: formData.selectedPermissions
          }, {
            headers: { loginToken: localStorage.getItem('loginToken') }
          });
        }

        showSuccessNotification('Role created successfully!');
      }

      // Close modal and refresh
      setShowModal(false);
      setEditingRole(null);
      setFormData({ roleName: '', selectedPermissions: [] });
    } catch (error) {
      console.error('Error saving role:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to save role';
      alert(errorMessage);
    }
  };

  const showSuccessNotification = (message) => {
    setSuccessMessage(message);
    setShowSuccessPopup(true);
    
    // Auto-close after 2.5 seconds
    setTimeout(() => {
      setShowSuccessPopup(false);
      // Refresh roles list after popup closes
      fetchRoles();
    }, 2500);
  };

  const handleEdit = async (role) => {
    setEditingRole(role);
    setFormData({ roleName: role.roleName, selectedPermissions: [] });

    // Fetch current permissions for this role
    try {
      const response = await axios.get(`${apiUrl}/permission/role/${role.id}`, {
        headers: { loginToken: localStorage.getItem('loginToken') }
      });
      setFormData(prev => ({
        ...prev,
        selectedPermissions: response.data.permissions.map(p => p.id)
      }));
    } catch (error) {
      console.error('Error fetching role permissions:', error);
    }

    setShowModal(true);
  };

  const handleDelete = (roleId, roleName) => {
    setDeleteConfirm({ show: true, roleId, roleName });
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${apiUrl}/userRole/deleteRole/${deleteConfirm.roleId}`, {
        headers: { loginToken: localStorage.getItem('loginToken') }
      });
      setDeleteConfirm({ show: false, roleId: null, roleName: '' });
      showSuccessNotification('Role deleted successfully!');
    } catch (error) {
      console.error('Error deleting role:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to delete role';
      setDeleteConfirm({ show: false, roleId: null, roleName: '' });
      alert(errorMessage);
    }
  };

  const handlePermissionChange = (permissionId, checked) => {
    setFormData(prev => ({
      ...prev,
      selectedPermissions: checked
        ? [...prev.selectedPermissions, permissionId]
        : prev.selectedPermissions.filter(id => id !== permissionId)
    }));
  };

  const renderPermissionsTree = () => {
    return Object.entries(permissions).map(([module, subModules]) => (
      <div key={module} className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
          <FaShieldAlt className="mr-2 text-blue-500" />
          {module}
        </h3>
        <div className="ml-4 space-y-2">
          {Object.entries(subModules).map(([subModule, perms]) => (
            <div key={subModule} className="border-l-2 border-gray-200 pl-4">
              <h4 className="font-medium text-gray-700 mb-2">{subModule}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {perms.map(permission => (
                  <label key={permission.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.selectedPermissions.includes(permission.id)}
                      onChange={(e) => handlePermissionChange(permission.id, e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-600">
                      {permission.displayName}
                      <span className="ml-1 px-1 py-0.5 bg-gray-100 text-xs rounded">
                        {permission.action}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    ));
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="w-full min-h-screen bg-gray-50">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 flex items-center">
              <FaUsers className="mr-4 text-blue-600 text-3xl" />
              Role Management
            </h1>
            <p className="text-gray-600 mt-2">Create and manage user roles with permissions</p>
          </div>
          <button
            onClick={() => {
              setEditingRole(null);
              setFormData({ roleName: '', selectedPermissions: [] });
              setShowModal(true);
            }}
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-lg flex items-center shadow-md hover:shadow-lg transition-all duration-200 whitespace-nowrap"
          >
            <FaPlus className="mr-2" />
            Add Role
          </button>
        </div>

        {/* Roles Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                  <th className="px-8 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-widest">
                    Role Name
                  </th>
                  <th className="px-8 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-widest">
                    Users Count
                  </th>
                  <th className="px-8 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-widest">
                    Permissions Count
                  </th>
                  <th className="px-8 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-widest">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {roles.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-8 py-12 text-center text-gray-500">
                      <p className="text-lg">No roles found. Create your first role to get started.</p>
                    </td>
                  </tr>
                ) : (
                  roles.map((role, index) => (
                    <tr
                      key={role.id}
                      className={`hover:bg-blue-50 transition-colors duration-150 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                    >
                      <td className="px-8 py-4 text-sm font-semibold text-gray-900">
                        <div className="flex items-center">
                          <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                          {role.roleName}
                        </div>
                      </td>
                      <td className="px-8 py-4 text-sm text-gray-600">
                        <span className="px-3 py-1 bg-gray-100 rounded-full text-gray-700 font-medium">{role.usersCount ?? 0}</span>
                      </td>
                      <td className="px-8 py-4 text-sm text-gray-600">
                        <span className="px-3 py-1 bg-gray-100 rounded-full text-gray-700 font-medium">{role.permissionsCount ?? 0}</span>
                      </td>
                      <td className="px-8 py-4 text-sm font-medium">
                        <div className="flex justify-center gap-3">
                          <button
                            onClick={() => handleEdit(role)}
                            className="text-blue-600 hover:text-blue-900 hover:bg-blue-100 p-2 rounded-lg transition-all duration-150"
                            title="Edit role"
                          >
                            <FaEdit size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(role.id, role.roleName)}
                            className="text-red-600 hover:text-red-900 hover:bg-red-100 p-2 rounded-lg transition-all duration-150"
                            title="Delete role"
                          >
                            <FaTrash size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-xl">
              <h2 className="text-2xl font-bold text-gray-800">
                {editingRole ? 'Edit Role' : 'Add New Role'}
              </h2>
              <button
                type="button"
                onClick={() => { setShowModal(false); setEditingRole(null); setFormData({ roleName: '', selectedPermissions: [] }); }}
                className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors duration-150"
                title="Close"
              >
                <FaTimes size={20} />
              </button>
            </div>

            <div className="p-6">
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Role Name
                </label>
                <input
                  type="text"
                  value={formData.roleName}
                  onChange={(e) => setFormData(prev => ({ ...prev, roleName: e.target.value }))}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Permissions
                </label>
                <div className="max-h-96 overflow-y-auto border rounded p-4">
                  {renderPermissionsTree()}
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingRole(null); setFormData({ roleName: '', selectedPermissions: [] }); }}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2 px-5 rounded-lg transition-colors duration-150"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-5 rounded-lg transition-colors duration-150"
                >
                  {editingRole ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-800">Confirm Delete</h3>
              <button
                onClick={() => setDeleteConfirm({ show: false, roleId: null, roleName: '' })}
                className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
              >
                <FaTimes size={16} />
              </button>
            </div>
            <div className="px-6 py-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <FaTrash className="text-red-600" size={20} />
                </div>
                <div>
                  <p className="text-gray-800 font-medium">Delete role <span className="font-bold text-red-600">"{deleteConfirm.roleName}"</span>?</p>
                  <p className="text-sm text-gray-500 mt-1">This action cannot be undone. Users with this role will lose their assigned role.</p>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setDeleteConfirm({ show: false, roleId: null, roleName: '' })}
                className="px-5 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold transition-colors duration-150"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-5 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold transition-colors duration-150 flex items-center gap-2"
              >
                <FaTrash size={14} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="fixed inset-0 bg-black opacity-30"></div>
          <div className="relative bg-white rounded-lg shadow-2xl px-8 py-6 flex flex-col items-center gap-4 animate-pulse">
            <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-full">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-800">{successMessage}</h3>
            <p className="text-sm text-gray-500">Redirecting to roles list...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManagement;